import React, { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ReactBB } from '@/common/utils/index';
import { useCurrentDecedentId } from '@/app/providers/DecedentProvider';
import { useSectionsQuestions, useLocale } from '@/common/hooks/index';
import MuiTable from '@/common/components/Table/index';
import styles from '@/common/containers/EditQuestionsAnswers/styles.less';
import { useHistory } from 'react-router-dom';
import { routes } from '@/app/routing/index';
import Loader from '@/common/components/Loader/index';
import { TASK_MODULES } from '@/common/constants/constants';
import { validateCondition } from '@/common/containers/FormConstructor/utils';
import { useQuestionListQuery } from '@/entities/question';

const Table = ({ module }) => {
  const { t } = useTranslation();
  const decedentId = useCurrentDecedentId();
  const history = useHistory();
  const { data: questionData, isLoading: loading } = useQuestionListQuery(
    module,
    decedentId
  );
  const { questions } = useSectionsQuestions(questionData?.list);
  const { isFr } = useLocale();
  const moduleLabel =
    module === TASK_MODULES.ASSET
      ? 'Assets'
      : module.charAt(0).toUpperCase() + module.slice(1);

  const shapedData = useMemo(() => {
    if (!questionData?.answers || !questionData?.list) return [];

    const answerObject = questionData.answers.reduce(
      (acc, { questionId, value, status }) => {
        if (status === 'active') acc[questionId] = value;
        return acc;
      },
      {}
    );

    const relativeAnswers = questionData.answers.reduce((acc, answer) => {
      const { slug, value, id, relatedAnswers = [], status, questionId } =
        answer;

      if (
        status === 'suspended' &&
        (!relatedAnswers.length || value === 'Yes' || value === 'No')
      ) {
        return acc;
      }

      const payload = { value, answerId: id, questionId };
      if (!relatedAnswers.length) {
        acc[slug] = payload;
        return acc;
      }

      if (!acc[slug]) acc[slug] = {};
      acc[slug][id || questionId] = payload;
      return acc;
    }, {});

    const isVisible = ({ condition }) => {
      if (!condition) return true;
      return Object.entries(condition).every(([fieldName, operation]) =>
        validateCondition(fieldName, operation, {}, relativeAnswers)
      );
    };

    const questionNotSure = [];
    let topNumber = 0;

    const collect = (arr, parentNumber) => {
      arr.forEach((item, index) => {
        if (!isVisible(item)) return;

        const number = parentNumber
          ? `${parentNumber}.${index + 1}`
          : `${(topNumber += 1)}`;

        if (answerObject[item.id] === 'Not Sure') {
          questionNotSure.push({
            ...item,
            number,
          });
        }

        if (item.children.length) collect(item.children, number);
      });
    };

    collect(questions);
    return questionNotSure;
  }, [questionData?.answers, questionData?.list, questions]);

  const tableData = useMemo(() => {
    return shapedData.map(({ id, content, contentFr, slug, number }) => ({
      key: id,
      data: [
        {
          value: number,
          cellProps: {
            sx: {
              width: '5%',
              fontSize: '18px',
              lineHeight: '24px',
              fontWeight: '300',
            },
          },
        },
        {
          value: ReactBB((isFr ? contentFr : content) ?? content),
          cellProps: {
            sx: {
              width: '75%',
              fontWeight: '500',
              fontSize: '18px',
              lineHeight: '24px',
            },
          },
        },
        {
          value: t('Go to question'),
          cellProps: {
            onClick: () =>
              history.push(
                `${routes.dashboard.editQuestions}?module=${module}&slug=${slug}`
              ),
            sx: {
              width: '20%',
              color: '#338F6C',
              fontSize: '16px',
              lineHeight: '24px',
              cursor: 'pointer',
            },
          },
        },
      ],
    }));
  }, [shapedData, isFr, t]);

  if (loading) return <Loader />;
  if (!shapedData.length) return null;
  return (
    <div className={styles.list}>
      <p className={styles.listLabel}>
        <Trans
          i18nKey="You have provided a &quot;Not Sure&quot; response to the questions below. Please review and provide a definitive &apos;Yes&apos; / &apos;No&apos; answer to these questions when you are able to in order to finalize the <1>{{module}} tasks</1> list. In the meantime, you can begin or continue working on the <1>{{module}} tasks</1>."
          values={{ module: moduleLabel }}
          components={{ 1: <b /> }}
        />
      </p>
      <div className={styles.listContainer}>
        <MuiTable
          column={[t('No.'), t('Question'), t('Action')]}
          data={tableData}
          stickyHeader={false}
          rowsNum={2}
        />
      </div>
    </div>
  );
};

export default Table;
